/**
 * The one place in this console where micro units and an amount a person reads meet —
 * in both directions since 2026-10-21: `microAmount`/`microMoney` print X's figure, and
 * `amountToMicro` turns a typed amount into the figure X is sent.
 *
 * 🔴 **Why it is a module of its own, and the only one.** Four test files forbid
 * arithmetic on money in the screens and copy of this SPA — `campaign-copy.test.ts`,
 * `line-item-copy.test.ts`, `targeting-copy.test.ts` and `campaigns.ts`'s own guard —
 * and the pattern they forbid (`/ 1e6`, `.toFixed(`, `Math.round(`) is exactly what an
 * amount needs. Those guards exist because a budget was once turned into money wrongly,
 * so they are not relaxed: the arithmetic moves HERE, where it is one function with its
 * own cases, and each guard's file set keeps naming the copy modules. One place to be
 * wrong instead of five.
 *
 * 🔴 **And it is integer arithmetic, not division.** `value / 1e6` is a float, and
 * `toFixed(2)` on a float rounds — which is how a spend of 19.999 becomes "20.00" and a
 * ceiling looks crossed when it is not, or the other way about. The digits are split
 * instead: a micro amount is an integer of millionths, so the major part is a quotient
 * and the minor part is the next two digits, **truncated and never rounded**. An
 * operator comparing against a ceiling is better served by a number that never claims
 * the cent it has not reached.
 *
 * Nothing here knows a currency's exponent: it prints two decimals because the
 * currencies this console has seen (EUR, USD) have two, and it says the code beside the
 * figure rather than a symbol, so a currency with a different exponent reads as itself
 * and not as a wrong amount in another.
 */

/** 10^6, the unit X states for every `*_local_micro` field. Named, not inlined. */
const MICRO_PER_UNIT = 1_000_000;

/** How many micro units one printed decimal place is worth, at two decimals. */
const MICRO_PER_MINOR = MICRO_PER_UNIT / 100;

/**
 * A micro amount as `12.34`, with no currency and no symbol.
 *
 * 🔴 Truncating, not rounding, and the sign is carried rather than lost: X documents
 * that `billed_charge_local_micro` can **decrease** after 24 hours through credits, so
 * a negative figure is a shape this console may be handed and must not print as its
 * absolute value.
 */
export function microAmount(micro: number): string {
	const negative = micro < 0;
	const whole = Math.abs(Math.trunc(micro));
	const major = Math.trunc(whole / MICRO_PER_UNIT);
	const minor = Math.trunc((whole - major * MICRO_PER_UNIT) / MICRO_PER_MINOR);
	return `${negative ? "-" : ""}${String(major)}.${String(minor).padStart(2, "0")}`;
}

/**
 * A micro amount with the currency code X holds for the account.
 *
 * 🔴 **The currency never comes from the answer this figure came in.** X's analytics
 * response carries `billed_charge_local_micro` and no currency field at all (the
 * reference's own example response, read 2026-10-08), so the code is read from the
 * account's funding instrument — a different object, already in this console — and a
 * null here prints the unit instead of guessing a currency. An amount labelled with the
 * wrong currency is worse than an amount labelled with none, because only the first
 * one looks finished.
 */
export function microMoney(micro: number, currency: string | null): string {
	return currency ? `${microAmount(micro)} ${currency}` : `${String(micro)} micro`;
}

/**
 * The other direction, for the one screen that takes an amount from a person: the
 * Create ad wizard's budget fields (2026-10-21). `"10"`, `"10.5"` and `"10.50"` are
 * `10_000_000` and `10_500_000`; anything else — a sign, a third decimal, a comma, a
 * word — is `null`, and the field says so rather than sending a guess.
 *
 * 🔴 Integer arithmetic again, and for the same reason as above: `Number("8.2") * 1e6`
 * is `8199999.999999999` (measured 2026-10-21, node 25.9; `money.test.ts` holds it),
 * which a `Math.round` would mend and the guards forbid.
 * The digits are split instead — the major part times the unit, the minor part padded
 * to two places times a hundredth — so the figure X receives is exactly the one typed.
 */
export function amountToMicro(text: string): number | null {
	const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(text.trim());
	if (m === null) return null;
	const major = Number(m[1]);
	const minor = Number((m[2] ?? "").padEnd(2, "0"));
	const micro = major * MICRO_PER_UNIT + minor * MICRO_PER_MINOR;
	return Number.isSafeInteger(micro) ? micro : null;
}
